import {
  ABILITY_CAST_COLORS as COLORS,
  ABILITY_CAST_HEART_OUTLINE_SCALE,
  ABILITY_CAST_PACE,
  ABILITY_CAST_TEXTURE_KEYS,
  GOBLINS_BOMB_CAST,
  GOBLINS_TRAP_CAST,
  INCOME_REVEAL_CAST,
  LEPRECHAUNS_THEFT_CAST,
  SEDUCTION_CAST,
  SUPPLY_BREAK_CAST,
  SUPPLY_TRANSFER_CAST,
  UNIT_SIZE_TRANSFER_CAST,
  UNIT_UPGRADE_CAST,
} from '../../configs/abilityCastAnimation';
import {COINS_SPENT_NUMBER} from '../../configs/coinsSpentAnimation';
import {RUINS_INCOME_ANIMATION} from '../../configs/ruinsIncomeAnimation';
import {MELEE_TEXTURE_KEYS} from '../../configs/meleeAnimation';
import {SIZE_RECRUITMENT_MARKER, SIZE_RECRUITMENT_TEXTURE_KEYS} from '../../configs/sizeRecruitmentMarker';
import {buildThreadGeometry, resolveThreadPoint, resolveThreadRevealMs} from './glowThreadGeometry';
import {CELL_OUTLINE_TEXTURE_KEYS} from '../../configs/fieldCellOutlines';
import {HEX_WAVE_TEXTURE_KEYS, resolveUnitRecruitmentCast} from '../../configs/unitRecruitmentAnimation';
import {HEX_WAVE_TEXTURE_SIZE, LIGHT_BEAM_ASPECT, resolveHexWaveScale} from './hexWaveTextures';

/**
 * ПЛАН АНИМАЦИИ СПОСОБНОСТИ — что, где и когда рисуется (см.
 * configs/abilityCastAnimation). Здесь только расчет: рисует план
 * scenes/AbilityCastAnimation, кадр за кадром спрашивая у каждого элемента
 * его позу.
 *
 * План:
 *  - totalMs — длительность целиком, impactMs — момент попадания: тогда на
 *    поле открывается итог способности и звучит его звук;
 *  - elements — спрайты: текстура, цвет (tint), размер на поле при масштабе 1
 *    (sizePx — ширина картинки), окно видимости [startMs, endMs), слой
 *    ('air' — над полем, 'ground' — на земле под отрядами, 'wings' — за иконкой
 *    летящего отряда), сложение цвета ('add' — светится), точка поворота origin
 *    {x, y} в долях картинки (по умолчанию центр) и pose(local, elapsedMs) →
 *    {x, y, scale | scaleX/scaleY, alpha, rotation, tint?, layer?}, где local —
 *    доля окна 0..1, а layer переводит спрайт на другой слой;
 *  - outlines — контуры ячейки (вспышка ловушки): точки относительно центра,
 *    цвет, толщина и та же поза;
 *  - shake — дрожь камеры (взрыв, повредивший или разрушивший строение).
 *
 * Случайность (разброс искр и клубов дыма) — только вид: источник rng задает
 * вызывающий, в тестах — заглушка. На игру она не влияет.
 */

const TWO_PI = Math.PI * 2;

export const clamp01 = (value) => Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));
export const lerp = (from, to, share) => from + (to - from) * share;

export const easeOutQuad = (share) => 1 - (1 - share) * (1 - share);
export const easeOutCubic = (share) => 1 - (1 - share) ** 3;
export const easeInOutSine = (share) => -(Math.cos(Math.PI * share) - 1) / 2;
// Выпрыгивание с перехлестом: к концу чуть больше 1 и обратно
export const easeOutBack = (share) => {
  const overshoot = 1.70158;
  const shifted = share - 1;
  return 1 + (overshoot + 1) * shifted ** 3 + overshoot * shifted ** 2;
};

// Видимость, которая плавно появляется за первую долю окна и гаснет за последнюю
export const fadeInOut = (local, inShare, outShare) => {
  if (local < inShare) {
    return local / inShare;
  }
  if (local > 1 - outShare) {
    return (1 - local) / outShare;
  }
  return 1;
};

// Гаснет со своей доли окна до конца
export const fadeOutFrom = (local, fromShare) => (local <= fromShare ? 1 : (1 - local) / (1 - fromShare));

/** Цвет между двумя (0x-числа) */
export const mixColor = (fromColor, toColor, share) => {
  const part = clamp01(share);
  const channel = (shift) => Math.round(lerp((fromColor >> shift) & 0xff, (toColor >> shift) & 0xff, part));
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
};

/**
 * Подъем дуги полета: доля расстояния, но в пределах долей ячейки — короткий
 * бросок не должен быть плоским, а дальний — улетать за кадр
 */
export const resolveArcLift = (distance, hexSize, {liftShare, minLiftFactor, maxLiftFactor}) => (
  Math.min(hexSize * maxLiftFactor, Math.max(hexSize * minLiftFactor, distance * liftShare))
);

/**
 * Точка на дуге полета (квадратичная кривая): вершина дуги поднята над
 * серединой отрезка примерно на lift
 */
export const resolveArcPoint = (from, to, lift, share) => {
  const part = clamp01(share);
  const controlX = (from.x + to.x) / 2;
  const controlY = (from.y + to.y) / 2 - 2 * lift;
  const rest = 1 - part;
  return {
    x: rest * rest * from.x + 2 * rest * part * controlX + part * part * to.x,
    y: rest * rest * from.y + 2 * rest * part * controlY + part * part * to.y,
  };
};

/**
 * Во сколько раз сжать анимацию чужого действия под темп: итог должен
 * открыться в пределах доли шага до следующего действия. null — даже сжатая
 * она не уложится: анимации нет, итог виден сразу. Свое действие и запись
 * матча (paceMs не задан) идут с обычной скоростью
 *
 * @param {number} impactMs
 * @param {number} [paceMs]
 * @returns {number|null}
 */
export const resolveAbilityCastTimeScale = (impactMs, paceMs) => {
  if (!Number.isFinite(paceMs) || paceMs <= 0) {
    return 1;
  }
  const {impactShareOfPace, minTimeScale} = ABILITY_CAST_PACE;
  const timeScale = Math.min(1, paceMs * impactShareOfPace / impactMs);
  return timeScale >= minTimeScale ? timeScale : null;
};

export const createElement = ({
  textureKey, tint = COLORS.white, sizePx, startMs, durationMs, layer = 'air', blend, order = 0, origin, pose,
}) => ({textureKey, tint, sizePx, startMs, endMs: startMs + durationMs, layer, blend, order, origin, pose});

const distanceBetween = (from, to) => Math.hypot(to.x - from.x, to.y - from.y);

/**
 * Сердечко с темным контуром: под ним та же картинка, чуть крупнее и темнее, —
 * на светлых подложках (песок, снег) розовое без контура бледнеет
 * @returns {Object[]} подложка контура и само сердечко
 */
const withHeartOutline = (element) => [
  {
    ...element,
    tint: COLORS.heartOutline,
    order: element.order - 0.5,
    blend: undefined,
    pose: (local, elapsedMs) => {
      const pose = element.pose(local, elapsedMs);
      return {...pose, scale: (pose.scale ?? 1) * ABILITY_CAST_HEART_OUTLINE_SCALE, tint: undefined};
    },
  },
  element,
];

/**
 * Брызги, летящие по параболе: из точки, со скоростью и тяготением (доли
 * ячейки в секунду и в секунду за секунду)
 */
const ballisticPoint = (origin, velocityX, velocityY, gravity, seconds) => ({
  x: origin.x + velocityX * seconds,
  y: origin.y + velocityY * seconds + gravity * seconds * seconds / 2,
});

/**
 * СОБЛАЗН СУККУБОВ
 *
 * @param {Object} params
 * @param {{body: {x, y}, ground: {x, y}}} params.attacker — суккуб
 * @param {{body: {x, y}, ground: {x, y}}} params.target — цель
 * @param {number} params.hexSize
 * @param {boolean} params.success
 * @param {function(): number} [params.rng]
 */
export const buildSeductionCastPlan = ({attacker, target, hexSize, success, rng = Math.random}) => {
  const {gather, flight, success: win, failure: lose} = SEDUCTION_CAST;
  const size = hexSize;
  const from = attacker.body;
  const to = target.body;
  const lift = resolveArcLift(distanceBetween(from, to), size, flight);
  const impactMs = flight.startMs + flight.durationMs;
  const flightPoint = (share) => resolveArcPoint(from, to, lift, easeInOutSine(clamp01(share)));
  const flightShareAt = (elapsedMs) => (elapsedMs - flight.startMs) / flight.durationMs;
  const elements = [];

  // Сияние под суккубом, пока он колдует
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.succubes, sizePx: size * gather.auraSizeFactor,
    startMs: 0, durationMs: impactMs, layer: 'ground',
    pose: (local, elapsedMs) => ({
      x: attacker.ground.x,
      y: attacker.ground.y,
      scaleX: 0.6 + 0.4 * easeOutQuad(clamp01(elapsedMs / 300)),
      scaleY: (0.6 + 0.4 * easeOutQuad(clamp01(elapsedMs / 300))) * 0.6,
      alpha: gather.auraAlpha * (elapsedMs < 300 ? elapsedMs / 300 : 1 - clamp01((elapsedMs - 700) / (impactMs - 700))),
    }),
  }));

  // Сердечки сходятся к суккубу по спирали
  for (let index = 0; index < gather.count; index += 1) {
    const startAngle = index / gather.count * TWO_PI + (rng() - 0.5) * 0.5;
    elements.push(...withHeartOutline(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.HEART, tint: index % 2 ? COLORS.heartLight : COLORS.heart,
      sizePx: size * gather.sizeFactor, startMs: 0, durationMs: gather.durationMs + 40, order: 1,
      pose: (local) => {
        const radius = size * lerp(gather.startRadiusFactor, gather.endRadiusFactor, local * local);
        const angle = startAngle + gather.turns * TWO_PI * local;
        return {
          x: from.x + Math.cos(angle) * radius,
          y: from.y + Math.sin(angle) * radius * 0.7,
          scale: 0.6 + 0.4 * local,
          alpha: fadeInOut(local, 0.3, 0.2),
        };
      },
    })));
  }

  // След: сердечки и искры, всплывающие за летящим сердцем
  for (let index = 0; index < flight.trailCount; index += 1) {
    const spawnMs = flight.startMs + index * flight.trailEveryMs;
    if (spawnMs >= impactMs) {
      break;
    }
    const origin = flightPoint(flightShareAt(spawnMs));
    const driftX = (rng() - 0.5) * size * 0.14;
    const spin = (rng() - 0.5) * 0.8;
    const isSpark = index % 3 === 0;
    elements.push(createElement({
      textureKey: isSpark ? MELEE_TEXTURE_KEYS.GLOW : ABILITY_CAST_TEXTURE_KEYS.HEART,
      tint: index % 2 ? COLORS.heartLight : COLORS.succubes,
      sizePx: size * flight.trailSizeFactor * (isSpark ? 1.4 : 1),
      startMs: spawnMs, durationMs: flight.trailLifeMs, order: 2,
      pose: (local) => ({
        x: origin.x + driftX * local,
        y: origin.y - size * flight.trailRiseFactor * local,
        scale: 1 - 0.6 * local,
        alpha: 0.9 * (1 - local),
        rotation: spin * local,
      }),
    }));
  }

  // Летящее сердце в розовом ореоле: бьется, как сердце
  const charmPose = (local) => {
    const point = flightPoint(local);
    const appear = local < 0.12 ? easeOutQuad(local / 0.12) : 1;
    const beat = 1 + flight.beatScale * Math.max(0, Math.sin(local * Math.PI * 2 * flight.beats)) ** 3;
    return {
      x: point.x,
      y: point.y,
      scale: appear * beat,
      rotation: Math.sign(to.x - from.x) * 0.2 * Math.sin(local * Math.PI),
    };
  };
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.succubes, sizePx: size * flight.glowSizeFactor,
    startMs: flight.startMs, durationMs: flight.durationMs, order: 3,
    pose: (local) => ({...charmPose(local), rotation: 0, alpha: 0.65}),
  }));
  elements.push(...withHeartOutline(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.HEART, tint: COLORS.heart, sizePx: size * flight.sizeFactor,
    startMs: flight.startMs, durationMs: flight.durationMs, order: 4,
    pose: charmPose,
  })));

  let tailMs;
  if (success) {
    tailMs = win.tailMs;
    // Вспышка и волна очарования по земле
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.heart, sizePx: size * win.burstSizeFactor,
      startMs: impactMs, durationMs: win.burstMs, order: 5,
      pose: (local) => ({x: to.x, y: to.y, scale: 0.3 + 0.9 * easeOutCubic(local), alpha: 1 - local}),
    }));
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: COLORS.succubes, sizePx: size * win.ringSizeFactor,
      startMs: impactMs, durationMs: win.ringMs, layer: 'ground',
      pose: (local) => {
        const scale = 0.15 + 0.85 * easeOutCubic(local);
        return {x: target.ground.x, y: target.ground.y, scaleX: scale, scaleY: scale * 0.55, alpha: 0.95 * (1 - local)};
      },
    }));
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.succubes, sizePx: size * win.auraSizeFactor,
      startMs: impactMs, durationMs: win.auraMs, layer: 'ground', order: 1,
      pose: (local) => ({
        x: target.ground.x,
        y: target.ground.y,
        scaleX: 1,
        scaleY: 0.6,
        alpha: 0.85 * (1 - local) * (0.75 + 0.25 * Math.cos(local * Math.PI * 4)),
      }),
    }));
    // Россыпь сердечек: разлетаются и всплывают
    for (let index = 0; index < win.heartsCount; index += 1) {
      const angle = index / win.heartsCount * TWO_PI + (rng() - 0.5) * 0.4;
      const distance = size * win.heartsSpreadFactor * (0.75 + 0.5 * rng());
      const tints = [COLORS.heart, COLORS.heartLight, COLORS.succubes];
      elements.push(...withHeartOutline(createElement({
        textureKey: ABILITY_CAST_TEXTURE_KEYS.HEART, tint: tints[index % tints.length],
        sizePx: size * win.heartsSizeFactor, startMs: impactMs, durationMs: win.heartsLifeMs, order: 6,
        pose: (local) => {
          const spread = easeOutCubic(local);
          return {
            x: to.x + Math.cos(angle) * distance * spread,
            y: to.y + Math.sin(angle) * distance * spread * 0.7 - size * win.heartsRiseFactor * local,
            scale: (0.5 + 0.5 * Math.min(1, local * 4)) * (1 - 0.3 * local),
            alpha: fadeOutFrom(local, 0.5),
            rotation: Math.cos(angle) * 0.35 * local,
          };
        },
      })));
    }
    // Большое сердце всплывает над покоренным отрядом
    const crownPose = (local) => {
      const elapsed = local * win.crownMs;
      const pop = elapsed < win.crownPopMs ? easeOutBack(elapsed / win.crownPopMs) : 1;
      const beat = 1 + 0.12 * Math.max(0, Math.sin((elapsed - win.crownPopMs) / 1000 * TWO_PI * 2.2)) ** 3;
      return {
        x: to.x,
        y: to.y - size * lerp(win.crownStartLiftFactor, win.crownEndLiftFactor, easeOutQuad(local)),
        scale: Math.max(0, pop) * beat,
        alpha: fadeOutFrom(local, 0.6),
      };
    };
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.succubes, sizePx: size * win.crownSizeFactor * 2.2,
      startMs: impactMs, durationMs: win.crownMs, order: 7,
      pose: (local) => ({...crownPose(local), alpha: 0.55 * crownPose(local).alpha}),
    }));
    elements.push(...withHeartOutline(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.HEART, tint: COLORS.heart, sizePx: size * win.crownSizeFactor,
      startMs: impactMs, durationMs: win.crownMs, order: 8,
      pose: crownPose,
    })));
  } else {
    tailMs = lose.tailMs;
    // Невидимый щит цели вспыхивает холодным кольцом
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: 0xf4f4ff, sizePx: size * lose.shieldSizeFactor,
      startMs: impactMs, durationMs: lose.shieldMs, order: 5,
      pose: (local) => ({x: to.x, y: to.y, scale: 0.35 + 0.65 * easeOutCubic(local), alpha: 0.85 * (1 - local)}),
    }));
    // Сердце раскалывается: половинки разлетаются, серея, и падают
    [[ABILITY_CAST_TEXTURE_KEYS.HEART_LEFT, -1], [ABILITY_CAST_TEXTURE_KEYS.HEART_RIGHT, 1]].forEach(([textureKey, side]) => {
      elements.push(...withHeartOutline(createElement({
        textureKey, tint: COLORS.heart, sizePx: size * flight.sizeFactor,
        startMs: impactMs, durationMs: lose.halvesMs, order: 6,
        pose: (local) => ({
          x: to.x + side * size * lose.halvesSpreadFactor * easeOutQuad(local),
          y: to.y + size * (lose.halvesFallFactor * local * local - lose.halvesHopFactor * local),
          rotation: side * lose.halvesTurn * local,
          alpha: fadeOutFrom(local, 0.5),
          tint: mixColor(COLORS.heart, COLORS.failGray, local * lose.halvesMs / lose.grayOutMs),
        }),
      })));
    });
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.PUFF, tint: COLORS.failSmoke, sizePx: size * lose.puffSizeFactor,
      startMs: impactMs + 60, durationMs: lose.puffMs, order: 4,
      pose: (local) => ({
        x: to.x,
        y: to.y - size * 0.15 * local,
        scale: 0.5 + 0.8 * easeOutQuad(local),
        alpha: 0.6 * (1 - local),
        rotation: 0.3 * local,
      }),
    }));
    // Гаснущие искры осыпаются вниз
    for (let index = 0; index < lose.sparksCount; index += 1) {
      const angle = -Math.PI * (0.15 + 0.7 * rng());
      const speed = size * (0.6 + 0.6 * rng());
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.heartLight, sizePx: size * lose.sparksSizeFactor,
        startMs: impactMs, durationMs: lose.sparksLifeMs, order: 7,
        pose: (local) => ({
          ...ballisticPoint(to, Math.cos(angle) * speed, Math.sin(angle) * speed, size * 3, local * lose.sparksLifeMs / 1000),
          scale: 1 - 0.5 * local,
          alpha: 1 - local,
          tint: mixColor(COLORS.heartLight, COLORS.failGray, local * 2),
        }),
      }));
    }
  }

  return {totalMs: impactMs + tailMs, impactMs, elements, outlines: []};
};

/**
 * Исход броска бомбы гоблинов
 *  - 'destroyed' — строение разрушено;
 *  - 'damaged' — повреждено;
 *  - 'deflected' — попадание без урона (крепость выдержала);
 *  - 'failed' — бросок не удался.
 */
export const GOBLINS_BOMB_OUTCOMES = {
  DESTROYED: 'destroyed',
  DAMAGED: 'damaged',
  DEFLECTED: 'deflected',
  FAILED: 'failed',
};

export const resolveGoblinsBombOutcome = ({success, destroyed, damageIncrement}) => {
  if (!success) {
    return GOBLINS_BOMB_OUTCOMES.FAILED;
  }
  if (destroyed) {
    return GOBLINS_BOMB_OUTCOMES.DESTROYED;
  }
  return damageIncrement === 0 ? GOBLINS_BOMB_OUTCOMES.DEFLECTED : GOBLINS_BOMB_OUTCOMES.DAMAGED;
};

const rotatePoint = (x, y, rotation) => ({
  x: x * Math.cos(rotation) - y * Math.sin(rotation),
  y: x * Math.sin(rotation) + y * Math.cos(rotation),
});

/**
 * БОМБА ГОБЛИНОВ (повреждение строения)
 *
 * @param {Object} params
 * @param {{body: {x, y}, ground: {x, y}}} params.attacker — гоблин
 * @param {{body: {x, y}, ground: {x, y}}} params.target — строение
 * @param {number} params.hexSize
 * @param {string} params.outcome — GOBLINS_BOMB_OUTCOMES
 * @param {function(): number} [params.rng]
 */
export const buildGoblinsBombCastPlan = ({attacker, target, hexSize, outcome, rng = Math.random}) => {
  const {windup, flight, explosion, destroyed, deflected, dud} = GOBLINS_BOMB_CAST;
  const size = hexSize;
  const bombSizePx = size * flight.sizeFactor;
  const launch = {x: attacker.body.x, y: attacker.body.y - size * windup.liftFactor};
  // Бомба падает в середину жетона, а не на уровень головы отряда на нем
  const to = target.center || target.body;
  const lift = resolveArcLift(distanceBetween(launch, to), size, flight);
  const impactMs = flight.startMs + flight.durationMs;
  const side = to.x >= launch.x ? 1 : -1;
  const elements = [];

  // Бомба целиком: над гоблином — выпрыгивает, потом летит по дуге, вращаясь
  const bombPose = (elapsedMs) => {
    if (elapsedMs < flight.startMs) {
      return {...launch, scale: Math.max(0, easeOutBack(clamp01(elapsedMs / windup.durationMs))), rotation: 0};
    }
    const share = clamp01((elapsedMs - flight.startMs) / flight.durationMs);
    return {...resolveArcPoint(launch, to, lift, share), scale: 1, rotation: side * flight.turns * TWO_PI * share};
  };
  const fusePoint = (pose) => {
    const offset = rotatePoint(flight.fuseOffsetX * bombSizePx, flight.fuseOffsetY * bombSizePx, pose.rotation);
    return {x: pose.x + offset.x * pose.scale, y: pose.y + offset.y * pose.scale};
  };
  const flightEndPose = bombPose(impactMs);

  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.BOMB, sizePx: bombSizePx, startMs: 0, durationMs: impactMs, order: 5,
    pose: (local, elapsedMs) => bombPose(elapsedMs),
  }));
  // Огонек фитиля
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.fireYellow, sizePx: size * flight.fuseGlowSizeFactor,
    startMs: windup.durationMs * 0.4, durationMs: impactMs - windup.durationMs * 0.4, order: 6,
    pose: (local, elapsedMs) => {
      const pose = bombPose(elapsedMs);
      return {
        ...fusePoint(pose),
        scale: pose.scale * (0.85 + 0.15 * Math.sin(elapsedMs * 0.07)),
        alpha: 0.75 + 0.25 * Math.sin(elapsedMs * 0.09) * Math.cos(elapsedMs * 0.053),
      };
    },
  }));
  // Искры фитиля сыплются за бомбой
  for (let index = 0; index < flight.sparksCount; index += 1) {
    const spawnMs = windup.durationMs * 0.5 + index * flight.sparksEveryMs;
    if (spawnMs >= impactMs) {
      break;
    }
    const origin = fusePoint(bombPose(spawnMs));
    const angle = -Math.PI * rng();
    const speed = size * (0.3 + 0.5 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: index % 2 ? COLORS.fireOrange : COLORS.fireYellow,
      sizePx: size * flight.sparksSizeFactor, startMs: spawnMs, durationMs: flight.sparksLifeMs, order: 4,
      pose: (local) => ({
        ...ballisticPoint(origin, Math.cos(angle) * speed, Math.sin(angle) * speed, size * 1.5, local * flight.sparksLifeMs / 1000),
        scale: 1 - 0.5 * local,
        alpha: 1 - local,
      }),
    }));
  }
  // Дымный след
  for (let index = 0; index < flight.smokeCount; index += 1) {
    const spawnMs = flight.startMs + index * flight.smokeEveryMs;
    if (spawnMs >= impactMs) {
      break;
    }
    const origin = bombPose(spawnMs);
    const spin = (rng() - 0.5) * 1.2;
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.PUFF, tint: COLORS.smoke, sizePx: size * flight.smokeSizeFactor,
      startMs: spawnMs, durationMs: flight.smokeLifeMs, order: 1,
      pose: (local) => ({
        x: origin.x,
        y: origin.y - size * 0.2 * local,
        scale: 0.5 + 0.9 * local,
        alpha: 0.35 * (1 - local),
        rotation: spin * local,
      }),
    }));
  }

  let tailMs;
  let shake = null;
  if (outcome === GOBLINS_BOMB_OUTCOMES.FAILED) {
    tailMs = dud.tailMs;
    const dudFadeFromMs = dud.fuseOutMs + dud.fadeDelayMs;
    // Бомба падает, подпрыгивает, чуть качнувшись, и замирает фитилем вверх:
    // так видно, как он тухнет
    const restPose = (elapsedMs) => {
      const sinceImpact = elapsedMs - impactMs;
      const hop = clamp01(sinceImpact / dud.hopMs);
      return {
        x: to.x + side * size * dud.driftFactor * hop,
        y: to.y - size * dud.hopFactor * Math.sin(Math.PI * hop),
        rotation: flightEndPose.rotation + side * dud.wobble * Math.sin(Math.PI * hop),
        scale: 1 - 0.15 * clamp01((sinceImpact - dudFadeFromMs) / dud.fadeMs),
      };
    };
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.BOMB, sizePx: bombSizePx,
      startMs: impactMs, durationMs: dudFadeFromMs + dud.fadeMs, order: 5,
      pose: (local, elapsedMs) => ({
        ...restPose(elapsedMs),
        alpha: 1 - clamp01((elapsedMs - impactMs - dudFadeFromMs) / dud.fadeMs),
      }),
    }));
    // Огонек фитиля мигает, съеживается и тухнет — бомба еще лежит
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.fireOrange, sizePx: size * flight.fuseGlowSizeFactor,
      startMs: impactMs, durationMs: dud.fuseOutMs, order: 6,
      pose: (local, elapsedMs) => ({
        ...fusePoint(restPose(elapsedMs)),
        scale: 1 - 0.75 * local,
        alpha: (1 - local) * (0.5 + 0.5 * Math.abs(Math.sin(elapsedMs * 0.13))),
      }),
    }));
    for (let index = 0; index < dud.sputterCount; index += 1) {
      const spawnMs = impactMs + 40 + index * 100;
      const origin = fusePoint(restPose(spawnMs));
      const angle = -Math.PI * (0.25 + 0.5 * rng());
      const speed = size * (0.3 + 0.3 * rng());
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.fireYellow, sizePx: size * flight.sparksSizeFactor,
        startMs: spawnMs, durationMs: 250, order: 7,
        pose: (local) => ({
          ...ballisticPoint(origin, Math.cos(angle) * speed, Math.sin(angle) * speed, size * 2, local * 0.25),
          alpha: 1 - local,
        }),
      }));
    }
    // Потухший фитиль дает тонкую струйку дыма
    const fuseOutPoint = fusePoint(restPose(impactMs + dud.fuseOutMs));
    for (let index = 0; index < dud.wispCount; index += 1) {
      const sway = (index % 2 ? 1 : -1) * size * 0.07;
      elements.push(createElement({
        textureKey: ABILITY_CAST_TEXTURE_KEYS.PUFF, tint: COLORS.failGray, sizePx: size * dud.wispSizeFactor,
        startMs: impactMs + dud.fuseOutMs - 60 + index * dud.wispEveryMs, durationMs: dud.wispLifeMs, order: 8,
        pose: (local) => ({
          x: fuseOutPoint.x + sway * Math.sin(local * Math.PI * 1.5),
          y: fuseOutPoint.y - size * 0.5 * local,
          scale: 0.5 + 0.9 * local,
          alpha: 0.7 * (1 - local),
        }),
      }));
    }
  } else {
    tailMs = explosion.tailMs;
    const isDestroyed = outcome === GOBLINS_BOMB_OUTCOMES.DESTROYED;
    const isDeflected = outcome === GOBLINS_BOMB_OUTCOMES.DEFLECTED;
    const blastScale = isDestroyed ? destroyed.scale : isDeflected ? deflected.scale : 1;
    const addBlast = (center, startMs, scale) => {
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.white, sizePx: size * explosion.flashSizeFactor * scale,
        startMs, durationMs: explosion.flashMs, order: 9, blend: 'add',
        pose: (local) => ({...center, scale: 0.5 + 0.5 * easeOutQuad(local), alpha: 1 - local}),
      }));
      const spin = rng() * TWO_PI;
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.fireOrange, sizePx: size * explosion.fireballSizeFactor * scale,
        startMs, durationMs: explosion.fireballMs, order: 8,
        pose: (local) => ({
          ...center,
          scale: 0.25 + 0.85 * easeOutCubic(local),
          alpha: fadeOutFrom(local, 0.35),
          rotation: spin + 0.3 * local,
        }),
      }));
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.fireYellow, sizePx: size * 0.9 * scale,
        startMs, durationMs: explosion.fireballMs * 0.8, order: 9,
        pose: (local) => ({...center, scale: 0.4 + 0.6 * easeOutCubic(local), alpha: 1 - local}),
      }));
    };
    addBlast(to, impactMs, blastScale);
    if (isDestroyed) {
      const secondAngle = rng() * TWO_PI;
      const secondCenter = {
        x: to.x + Math.cos(secondAngle) * size * destroyed.secondBlastOffsetFactor,
        y: to.y + Math.sin(secondAngle) * size * destroyed.secondBlastOffsetFactor,
      };
      addBlast(secondCenter, impactMs + destroyed.secondBlastDelayMs, blastScale * 0.75);
    }
    // Крепость выдержала — маленький взрыв камеру не трогает
    if (!isDeflected) {
      shake = isDestroyed
        ? {atMs: impactMs, durationMs: destroyed.shakeMs, intensity: destroyed.shakeIntensity}
        : {atMs: impactMs, durationMs: explosion.shakeMs, intensity: explosion.shakeIntensity};
    }
    // ВЗРЫВ ВИДЕН СВЕРХУ, как и все поле: волна — ровный круг, дым и угольки
    // расходятся по кругу от центра и не падают вниз экрана. Под ним — выжженное пятно
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.darkSmoke, sizePx: size * explosion.scorchSizeFactor * blastScale,
      startMs: impactMs, durationMs: explosion.scorchMs, layer: 'ground', order: -1,
      pose: (local) => ({...to, scale: 0.6 + 0.4 * easeOutCubic(Math.min(1, local * 5)), alpha: 0.7 * (1 - local)}),
    }));
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: COLORS.fireYellow, sizePx: size * explosion.ringSizeFactor * blastScale,
      startMs: impactMs, durationMs: explosion.ringMs, layer: 'ground',
      pose: (local) => {
        const scale = 0.1 + 0.9 * easeOutCubic(local);
        return {...to, scale, alpha: 0.9 * (1 - local)};
      },
    }));
    // Клубы дыма расходятся от центра
    const smokeCount = isDestroyed ? destroyed.smokeCount : isDeflected ? 3 : explosion.smokeCount;
    for (let index = 0; index < smokeCount; index += 1) {
      const angle = index / smokeCount * TWO_PI + (rng() - 0.5) * 0.6;
      const distance = size * blastScale * lerp(explosion.smokeMinSpreadFactor, explosion.smokeMaxSpreadFactor, rng());
      const sizeJitter = 0.8 + 0.4 * rng();
      const spin = (rng() - 0.5) * 0.8;
      elements.push(createElement({
        textureKey: ABILITY_CAST_TEXTURE_KEYS.PUFF, tint: index % 2 ? COLORS.smoke : COLORS.darkSmoke,
        sizePx: size * explosion.smokeSizeFactor * blastScale * sizeJitter,
        startMs: impactMs + explosion.smokeDelayMs, durationMs: explosion.smokeLifeMs, order: 7,
        pose: (local) => {
          const spread = easeOutCubic(local);
          return {
            x: to.x + Math.cos(angle) * distance * spread,
            y: to.y + Math.sin(angle) * distance * spread,
            scale: 0.6 + 0.8 * spread,
            alpha: 0.92 * (local < 0.15 ? local / 0.15 : 1 - (local - 0.15) / 0.85),
            rotation: spin * local,
          };
        },
      }));
    }
    // Угольки и обломки разлетаются во все стороны, замедляясь
    const embersCount = isDestroyed ? destroyed.embersCount : isDeflected ? 0 : explosion.embersCount;
    const emberTints = [COLORS.fireYellow, COLORS.fireOrange, COLORS.fireRed];
    for (let index = 0; index < embersCount; index += 1) {
      const angle = index / embersCount * TWO_PI + (rng() - 0.5) * 0.5;
      const distance = size * lerp(explosion.embersMinSpreadFactor, explosion.embersMaxSpreadFactor, rng()) * blastScale;
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: emberTints[index % emberTints.length],
        sizePx: size * explosion.embersSizeFactor, startMs: impactMs, durationMs: explosion.embersLifeMs, order: 10,
        pose: (local) => ({
          x: to.x + Math.cos(angle) * distance * easeOutCubic(local),
          y: to.y + Math.sin(angle) * distance * easeOutCubic(local),
          scale: 1 - 0.6 * local,
          alpha: fadeOutFrom(local, 0.6),
        }),
      }));
    }
  }

  return {totalMs: impactMs + tailMs, impactMs, elements, outlines: [], shake};
};

// Восходящий поток искр по спирали вокруг отряда: вылетают струей, каждая четвертая —
// мягкая искра, остальные — звездочки
export const pushRisingSpiral = (elements, {centerX, groundY, topY, size, particles, tints, order, rng}) => {
  for (let index = 0; index < particles.count; index += 1) {
    const startPhase = rng() * TWO_PI;
    const particleTopY = topY + (rng() - 0.5) * size * particles.topJitterFactor;
    const isSpark = index % 4 === 3;
    elements.push(createElement({
      textureKey: isSpark ? MELEE_TEXTURE_KEYS.GLOW : MELEE_TEXTURE_KEYS.BURST,
      tint: tints[index % tints.length],
      sizePx: size * lerp(particles.minSizeFactor, particles.maxSizeFactor, rng()),
      startMs: index / particles.count * particles.spawnMs, durationMs: particles.lifeMs, order,
      pose: (local) => {
        const phase = startPhase + particles.turns * TWO_PI * local;
        const radius = size * lerp(particles.startRadiusFactor, particles.endRadiusFactor, local);
        return {
          x: centerX + Math.cos(phase) * radius,
          y: lerp(groundY, particleTopY, easeOutQuad(local)) + Math.sin(phase) * radius * particles.swirlFlatten,
          scale: Math.min(1, local / 0.15) * (1 - 0.4 * local),
          alpha: fadeInOut(local, 0.15, 0.35),
          rotation: phase,
        };
      },
    }));
  }
};

/**
 * КРАЖА ЛЕПРЕКОНОВ: восходящий поток искр вокруг отряда-вора. Поднимается до той
 * же высоты и за то же время, что и всплывающее «+N» (COINS_SPENT_NUMBER), —
 * число идет на гребне потока. Попадания нет: момент итога — самое начало
 *
 * @param {Object} params
 * @param {{center: {x, y}, ground: {x, y}}} params.unit — отряд-вор
 * @param {number} params.hexSize
 * @param {function(): number} [params.rng]
 */
export const buildLeprechaunsTheftCastPlan = ({unit, hexSize, rng = Math.random}) => {
  const {totalMs, particles, column, ring} = LEPRECHAUNS_THEFT_CAST;
  const size = hexSize;
  const {center, ground} = unit;
  const topY = center.y - size * (COINS_SPENT_NUMBER.startLiftFactor + COINS_SPENT_NUMBER.riseFactor);
  const elements = [];

  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: COLORS.leprechaunGold, sizePx: size * ring.sizeFactor,
    startMs: 0, durationMs: ring.durationMs, layer: 'ground',
    pose: (local) => ({...ground, scale: 0.2 + 0.8 * easeOutCubic(local), alpha: ring.alpha * (1 - local)}),
  }));
  // Столб света растет вверх от ног отряда
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.leprechaunMint, sizePx: size * column.sizeFactor,
    startMs: 0, durationMs: column.durationMs, order: 1,
    pose: (local) => {
      const grow = easeOutQuad(Math.min(1, local * 2));
      return {
        x: center.x,
        y: lerp(ground.y, (ground.y + topY) / 2, grow),
        scaleX: 0.7,
        scaleY: 0.5 + (column.stretch - 0.5) * grow,
        alpha: column.alpha * fadeInOut(local, 0.15, 0.5),
      };
    },
  }));
  // Золотые и изумрудные звездочки, между ними светлые искры
  pushRisingSpiral(elements, {
    centerX: center.x, groundY: ground.y, topY, size, particles, order: 2, rng,
    tints: [COLORS.leprechaunGold, COLORS.leprechaunGreen, COLORS.leprechaunGold, COLORS.leprechaunMint],
  });

  return {totalMs, impactMs: 0, elements, outlines: []};
};

/**
 * УЛУЧШЕНИЕ ОТРЯДА: момент попадания (impactMs) — когда шевроны и светящийся
 * диск закрыли отряд: тогда на поле и меняется его иконка
 *
 * @param {Object} params
 * @param {{center: {x, y}, ground: {x, y}}} params.unit
 * @param {number} params.hexSize
 * @param {function(): number} [params.rng]
 */
export const buildUnitUpgradeCastPlan = ({unit, hexSize, rng = Math.random}) => {
  const {impactMs, totalMs, chevrons, cover, flash, rays, aura, rings, stream, burst} = UNIT_UPGRADE_CAST;
  const size = hexSize;
  const {center, ground} = unit;
  const elements = [];

  const chevronsPose = (elapsedMs) => {
    if (elapsedMs < impactMs) {
      const share = elapsedMs / impactMs;
      return {
        x: center.x,
        y: center.y + size * chevrons.startOffsetFactor * (1 - easeOutCubic(share)),
        scale: lerp(chevrons.fromScale, chevrons.peakScale, easeOutCubic(share)),
        alpha: clamp01(elapsedMs / chevrons.appearMs),
      };
    }
    const share = (elapsedMs - impactMs) / (totalMs - impactMs);
    const leave = clamp01((share - chevrons.holdShare) / (1 - chevrons.holdShare));
    return {
      x: center.x,
      y: center.y - size * chevrons.endLiftFactor * leave * leave,
      scale: lerp(chevrons.peakScale, 1, easeOutQuad(clamp01(share * 3))),
      alpha: fadeOutFrom(share, chevrons.fadeFromShare),
    };
  };

  // Золотое свечение по земле нарастает к смене иконки и долго гаснет
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.upgradeGold, sizePx: size * aura.sizeFactor,
    startMs: 0, durationMs: totalMs, layer: 'ground',
    pose: (local, elapsedMs) => ({
      ...ground,
      scale: 0.6 + 0.4 * easeOutQuad(clamp01(elapsedMs / impactMs)),
      alpha: aura.alpha * (elapsedMs < impactMs ? elapsedMs / impactMs : 1 - (elapsedMs - impactMs) / (totalMs - impactMs)),
    }),
  }));
  [0, rings.secondDelayMs].forEach((delayMs, index) => {
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: index ? COLORS.upgradeLight : COLORS.upgradeGold,
      sizePx: size * rings.sizeFactor, startMs: impactMs + delayMs, durationMs: rings.durationMs, layer: 'ground', order: 1,
      pose: (local) => ({...center, scale: 0.15 + 0.85 * easeOutCubic(local), alpha: rings.alpha * (1 - local)}),
    }));
  });
  // Лучи медленно вращаются за шевронами
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.upgradeAmber, sizePx: size * rays.sizeFactor,
    startMs: rays.startMs, durationMs: rays.durationMs, order: 1,
    pose: (local, elapsedMs) => ({
      ...center,
      scale: 0.35 + 0.65 * easeOutCubic(clamp01((elapsedMs - rays.startMs) / (impactMs - rays.startMs))),
      alpha: rays.alpha * fadeInOut(local, 0.2, 0.55),
      rotation: rays.turn * local,
    }),
  }));
  // Диск прячет смену иконки: к impactMs он непрозрачен
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: COLORS.upgradeLight, sizePx: size * cover.sizeFactor,
    startMs: cover.appearFromMs, durationMs: cover.goneAtMs - cover.appearFromMs, order: 2,
    pose: (local, elapsedMs) => ({
      ...center,
      alpha: elapsedMs < cover.fullAtMs
        ? clamp01((elapsedMs - cover.appearFromMs) / (cover.fullAtMs - cover.appearFromMs))
        : 1 - clamp01((elapsedMs - cover.fullUntilMs) / (cover.goneAtMs - cover.fullUntilMs)),
    }),
  }));
  // Шевроны в золотом ореоле
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.upgradeGold, sizePx: size * chevrons.glowSizeFactor,
    startMs: 0, durationMs: totalMs, order: 3,
    pose: (local, elapsedMs) => {
      const pose = chevronsPose(elapsedMs);
      return {...pose, alpha: chevrons.glowAlpha * pose.alpha};
    },
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.UPGRADE_CHEVRONS, sizePx: size * chevrons.sizeFactor,
    startMs: 0, durationMs: totalMs, order: 5,
    pose: (local, elapsedMs) => chevronsPose(elapsedMs),
  }));
  // Вспышка поверх всего в самый момент смены
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.white, sizePx: size * flash.sizeFactor,
    startMs: impactMs - flash.leadMs, durationMs: flash.durationMs, order: 6, blend: 'add',
    pose: (local) => ({...center, scale: 0.6 + 0.4 * easeOutQuad(local), alpha: 1 - local}),
  }));
  pushRisingSpiral(elements, {
    centerX: center.x, groundY: ground.y, topY: center.y - size * stream.topLiftFactor, size, particles: stream, order: 7, rng,
    tints: [COLORS.upgradeGold, COLORS.upgradeLight, COLORS.upgradeAmber, COLORS.white],
  });
  // Звездочки разлетаются по кругу
  for (let index = 0; index < burst.count; index += 1) {
    const angle = index / burst.count * TWO_PI + (rng() - 0.5) * 0.4;
    const distance = size * burst.spreadFactor * (0.7 + 0.5 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.upgradeLight : COLORS.upgradeGold,
      sizePx: size * burst.sizeFactor, startMs: impactMs, durationMs: burst.lifeMs, order: 7,
      pose: (local) => ({
        x: center.x + Math.cos(angle) * distance * easeOutCubic(local),
        y: center.y + Math.sin(angle) * distance * easeOutCubic(local),
        scale: 1 - 0.5 * local,
        alpha: fadeOutFrom(local, 0.45),
        rotation: angle + local * 2,
      }),
    }));
  }

  return {totalMs, impactMs, elements, outlines: []};
};

/**
 * ПЕРЕДАЧА РАЗМЕРА от квартала отряду: момент попадания (impactMs) — последний шар
 * долетел: тогда у отряда появляется «+N»
 *
 * @param {Object} params
 * @param {{center: {x, y}}} params.quarter — квартал
 * @param {{center: {x, y}, ground: {x, y}}} params.unit — отряд
 * @param {number} params.hexSize
 * @param {number} [params.amount] — сколько размера передано
 * @param {function(): number} [params.rng]
 */
export const buildUnitSizeTransferCastPlan = ({quarter, unit, hexSize, amount = 1, rng = Math.random}) => {
  const {launchMs, flightMs, orbGapMs, maxOrbs, tailMs, source, orb, trail, comet, arrival} = UNIT_SIZE_TRANSFER_CAST;
  const size = hexSize;
  const from = quarter.center;
  const to = unit.center;
  const orbCount = Math.min(maxOrbs, Math.max(1, Math.round(amount) || 1));
  const impactMs = launchMs + flightMs + (orbCount - 1) * orbGapMs;
  const lift = resolveArcLift(distanceBetween(from, to), size, orb.arc);
  const flightPoint = (share) => resolveArcPoint(from, to, lift, easeInOutSine(share));
  const elements = [];

  // КВАРТАЛ. Опустевший медальон разгорается и растворяется в свете
  elements.push(createElement({
    textureKey: SIZE_RECRUITMENT_TEXTURE_KEYS.PLATE, sizePx: size * SIZE_RECRUITMENT_MARKER.plateSizeFactor,
    startMs: 0, durationMs: source.plateMs, order: 1,
    pose: (local) => ({...from, scale: 1 + source.plateGrow * easeOutQuad(local), alpha: 1 - local}),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.upgradeGold, sizePx: size * source.aura.sizeFactor,
    startMs: 0, durationMs: source.aura.durationMs, layer: 'ground',
    pose: (local) => ({...from, scale: 0.75 + 0.25 * easeOutQuad(local), alpha: source.aura.alpha * fadeOutFrom(local, 0.3)}),
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: COLORS.upgradeGold, sizePx: size * source.ring.sizeFactor,
    startMs: 0, durationMs: source.ring.durationMs, layer: 'ground', order: 1,
    pose: (local) => ({...from, scale: 0.2 + 0.8 * easeOutCubic(local), alpha: source.ring.alpha * (1 - local)}),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.upgradeLight, sizePx: size * source.flash.sizeFactor,
    startMs: 0, durationMs: source.flash.durationMs, order: 2,
    pose: (local) => ({...from, scale: 0.6 + 0.4 * easeOutQuad(local), alpha: source.flash.alpha * (1 - local)}),
  }));
  for (let index = 0; index < source.motes.count; index += 1) {
    const angle = index / source.motes.count * TWO_PI + (rng() - 0.5) * 0.5;
    const distance = size * source.motes.spreadFactor * (0.75 + 0.4 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.upgradeLight : COLORS.upgradeGold,
      sizePx: size * source.motes.sizeFactor, startMs: 0, durationMs: launchMs + 40, order: 3,
      pose: (local) => {
        const swirl = angle + source.motes.turn * local;
        const radius = distance * (1 - easeInOutSine(local));
        return {
          x: from.x + Math.cos(swirl) * radius,
          y: from.y + Math.sin(swirl) * radius,
          scale: 0.6 + 0.6 * local,
          alpha: fadeInOut(local, 0.25, 0.1),
          rotation: swirl,
        };
      },
    }));
  }

  // ПОЛЕТ. Шар с человечками: лучи за ним вращаются, поверх светлое ядро
  for (let orbIndex = 0; orbIndex < orbCount; orbIndex += 1) {
    const startMs = orbIndex * orbGapMs;
    const orbOrder = 10 + orbIndex;
    const flightShare = (elapsedMs) => clamp01((elapsedMs - startMs - launchMs) / flightMs);
    const orbPose = (elapsedMs) => {
      const elapsedInOrb = elapsedMs - startMs;
      const born = clamp01(elapsedInOrb / launchMs);
      const share = flightShare(elapsedMs);
      return {
        ...flightPoint(share),
        scale: lerp(orb.bornScale, 1, easeOutBack(born)) * lerp(1, orb.landingScale, share * share),
        alpha: clamp01(elapsedInOrb / (launchMs * 0.5)),
      };
    };
    const durationMs = launchMs + flightMs;

    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.upgradeGold, sizePx: size * orb.glowSizeFactor,
      startMs, durationMs, order: orbOrder,
      pose: (local, elapsedMs) => {
        const pose = orbPose(elapsedMs);
        return {...pose, alpha: orb.glowAlpha * pose.alpha};
      },
    }));
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.upgradeAmber, sizePx: size * orb.raysSizeFactor,
      startMs, durationMs, order: orbOrder + 0.1,
      pose: (local, elapsedMs) => {
        const pose = orbPose(elapsedMs);
        return {...pose, alpha: orb.raysAlpha * pose.alpha, rotation: orb.raysTurn * local};
      },
    }));
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: COLORS.upgradeLight, sizePx: size * orb.coreSizeFactor,
      startMs, durationMs, order: orbOrder + 0.2,
      pose: (local, elapsedMs) => orbPose(elapsedMs),
    }));
    elements.push(createElement({
      textureKey: SIZE_RECRUITMENT_TEXTURE_KEYS.PEOPLE, tint: orb.iconTint, sizePx: size * orb.iconSizeFactor,
      startMs, durationMs, order: orbOrder + 0.3,
      pose: (local, elapsedMs) => orbPose(elapsedMs),
    }));

    for (let index = 0; index < comet.count; index += 1) {
      const share = (index + 0.5) / comet.count;
      const point = flightPoint(share);
      elements.push(createElement({
        textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: index % 2 ? COLORS.upgradeGold : COLORS.upgradeAmber,
        sizePx: size * lerp(comet.toSizeFactor, comet.fromSizeFactor, share),
        startMs: startMs + launchMs + share * flightMs, durationMs: comet.lifeMs, order: 4,
        pose: (local) => ({...point, scale: 1 - 0.5 * local, alpha: 1 - local}),
      }));
    }

    // След остается там, где шар уже прошел
    for (let index = 0; index < trail.count; index += 1) {
      const share = (index + 0.5) / trail.count;
      const point = flightPoint(share);
      const jitter = () => (rng() - 0.5) * size * trail.jitterFactor;
      const x = point.x + jitter();
      const y = point.y + jitter();
      const isSpark = index % 3 === 2;
      const spin = rng() * TWO_PI;
      elements.push(createElement({
        textureKey: isSpark ? MELEE_TEXTURE_KEYS.GLOW : MELEE_TEXTURE_KEYS.BURST,
        tint: [COLORS.upgradeGold, COLORS.upgradeLight, COLORS.upgradeAmber][index % 3],
        sizePx: size * lerp(trail.minSizeFactor, trail.maxSizeFactor, rng()),
        startMs: startMs + launchMs + share * flightMs, durationMs: trail.lifeMs, order: 5,
        pose: (local) => ({
          x,
          y: y - size * trail.driftFactor * local,
          scale: 1 - 0.65 * local,
          alpha: fadeOutFrom(local, 0.25),
          rotation: spin + local * 2,
        }),
      }));
    }
  }

  // ОТРЯД. Энергия пришла: вспышка, кольца и восходящий поток
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.upgradeGold, sizePx: size * arrival.aura.sizeFactor,
    startMs: impactMs - arrival.flash.leadMs, durationMs: arrival.aura.durationMs, layer: 'ground',
    pose: (local) => ({...unit.ground, scale: 0.7 + 0.3 * easeOutQuad(local), alpha: arrival.aura.alpha * fadeOutFrom(local, 0.25)}),
  }));
  [0, arrival.rings.secondDelayMs].forEach((delayMs, index) => {
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: index ? COLORS.upgradeLight : COLORS.upgradeGold,
      sizePx: size * arrival.rings.sizeFactor, startMs: impactMs + delayMs, durationMs: arrival.rings.durationMs, layer: 'ground', order: 1,
      pose: (local) => ({...to, scale: 0.15 + 0.85 * easeOutCubic(local), alpha: arrival.rings.alpha * (1 - local)}),
    }));
  });
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.upgradeAmber, sizePx: size * arrival.rays.sizeFactor,
    startMs: impactMs, durationMs: arrival.rays.durationMs, order: 20,
    pose: (local) => ({
      ...to,
      scale: 0.4 + 0.6 * easeOutCubic(local),
      alpha: arrival.rays.alpha * fadeOutFrom(local, 0.3),
      rotation: arrival.rays.turn * local,
    }),
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: COLORS.upgradeGold, sizePx: size * arrival.disc.sizeFactor,
    startMs: impactMs - arrival.flash.leadMs, durationMs: arrival.disc.durationMs, order: 20.5,
    pose: (local) => ({...to, scale: 0.7 + 0.3 * easeOutQuad(local), alpha: arrival.disc.alpha * (1 - local)}),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.white, sizePx: size * arrival.flash.sizeFactor,
    startMs: impactMs - arrival.flash.leadMs, durationMs: arrival.flash.durationMs, order: 21, blend: 'add',
    pose: (local) => ({...to, scale: 0.55 + 0.45 * easeOutQuad(local), alpha: 1 - local}),
  }));
  const streamElements = [];
  pushRisingSpiral(streamElements, {
    centerX: to.x, groundY: unit.ground.y, topY: to.y - size * arrival.stream.topLiftFactor, size, particles: arrival.stream, order: 22, rng,
    tints: [COLORS.upgradeGold, COLORS.upgradeLight, COLORS.upgradeAmber, COLORS.white],
  });
  streamElements.forEach((element) => {
    elements.push({...element, startMs: element.startMs + impactMs, endMs: element.endMs + impactMs});
  });
  for (let index = 0; index < arrival.burst.count; index += 1) {
    const angle = index / arrival.burst.count * TWO_PI + (rng() - 0.5) * 0.4;
    const distance = size * arrival.burst.spreadFactor * (0.7 + 0.5 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.upgradeLight : COLORS.upgradeGold,
      sizePx: size * arrival.burst.sizeFactor, startMs: impactMs, durationMs: arrival.burst.lifeMs, order: 22,
      pose: (local) => ({
        x: to.x + Math.cos(angle) * distance * easeOutCubic(local),
        y: to.y + Math.sin(angle) * distance * easeOutCubic(local),
        scale: 1 - 0.5 * local,
        alpha: fadeOutFrom(local, 0.45),
        rotation: angle + local * 2,
      }),
    }));
  }

  return {totalMs: impactMs + tailMs, impactMs, elements, outlines: []};
};

/**
 * ПЕРЕДАЧА СНАБЖЕНИЯ от фермы отряду: шар идет по ломаной пути за то же время, что
 * по ней тянется нить
 *
 * @param {Object} params
 * @param {Array<{x, y}>} params.points — путь от фермы до отряда
 * @param {number} params.hexSize
 * @param {string} [params.iconTextureKey] — иконка на шаре
 * @param {function(): number} [params.rng]
 * @returns {?Object} план; null — пути нет
 */
export const buildSupplyTransferCastPlan = ({points, hexSize, iconTextureKey = 'supply', rng = Math.random}) => {
  const geometry = buildThreadGeometry(points);
  if (geometry.length <= 0) {
    return null;
  }
  const {tailMs, orb, comet, trail, arrival} = SUPPLY_TRANSFER_CAST;
  const size = hexSize;
  const impactMs = resolveThreadRevealMs(geometry.length, hexSize);
  const to = points[points.length - 1];
  const pointAtShare = (share) => resolveThreadPoint(geometry, geometry.length * share);
  const elements = [];

  const orbPose = (elapsedMs) => ({
    ...pointAtShare(clamp01(elapsedMs / impactMs)),
    scale: lerp(orb.bornScale, 1, easeOutBack(clamp01(elapsedMs / orb.bornMs))),
    alpha: clamp01(elapsedMs / (orb.bornMs * 0.5)),
  });
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.supplyAmber, sizePx: size * orb.glowSizeFactor,
    startMs: 0, durationMs: impactMs, order: 10,
    pose: (local, elapsedMs) => {
      const pose = orbPose(elapsedMs);
      return {...pose, alpha: orb.glowAlpha * pose.alpha};
    },
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.supply, sizePx: size * orb.raysSizeFactor,
    startMs: 0, durationMs: impactMs, order: 10.1,
    pose: (local, elapsedMs) => {
      const pose = orbPose(elapsedMs);
      return {...pose, alpha: orb.raysAlpha * pose.alpha, rotation: orb.raysTurn * local};
    },
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: COLORS.supplyLight, sizePx: size * orb.coreSizeFactor,
    startMs: 0, durationMs: impactMs, order: 10.2,
    pose: (local, elapsedMs) => orbPose(elapsedMs),
  }));
  elements.push(createElement({
    textureKey: iconTextureKey, sizePx: size * orb.iconSizeFactor,
    startMs: 0, durationMs: impactMs, order: 10.3,
    pose: (local, elapsedMs) => orbPose(elapsedMs),
  }));

  for (let index = 0; index < comet.count; index += 1) {
    const share = (index + 0.5) / comet.count;
    const point = pointAtShare(share);
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: index % 2 ? COLORS.supply : COLORS.supplyAmber,
      sizePx: size * lerp(comet.toSizeFactor, comet.fromSizeFactor, share),
      startMs: share * impactMs, durationMs: comet.lifeMs, order: 4,
      pose: (local) => ({...point, scale: 1 - 0.5 * local, alpha: 1 - local}),
    }));
  }
  for (let index = 0; index < trail.count; index += 1) {
    const share = (index + 0.5) / trail.count;
    const point = pointAtShare(share);
    const x = point.x + (rng() - 0.5) * size * trail.jitterFactor;
    const y = point.y + (rng() - 0.5) * size * trail.jitterFactor;
    const spin = rng() * TWO_PI;
    elements.push(createElement({
      textureKey: index % 3 === 2 ? MELEE_TEXTURE_KEYS.GLOW : MELEE_TEXTURE_KEYS.BURST,
      tint: [COLORS.supplyAmber, COLORS.supplyLight, COLORS.supply][index % 3],
      sizePx: size * lerp(trail.minSizeFactor, trail.maxSizeFactor, rng()),
      startMs: share * impactMs, durationMs: trail.lifeMs, order: 5,
      pose: (local) => ({
        x,
        y: y - size * trail.driftFactor * local,
        scale: 1 - 0.65 * local,
        alpha: fadeOutFrom(local, 0.25),
        rotation: spin + local * 2,
      }),
    }));
  }

  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.supply, sizePx: size * arrival.aura.sizeFactor,
    startMs: impactMs, durationMs: arrival.aura.durationMs, layer: 'ground',
    pose: (local) => ({...to, scale: 0.7 + 0.3 * easeOutQuad(local), alpha: arrival.aura.alpha * fadeOutFrom(local, 0.25)}),
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: COLORS.supplyAmber, sizePx: size * arrival.ring.sizeFactor,
    startMs: impactMs, durationMs: arrival.ring.durationMs, layer: 'ground', order: 1,
    pose: (local) => ({...to, scale: 0.15 + 0.85 * easeOutCubic(local), alpha: arrival.ring.alpha * (1 - local)}),
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: COLORS.supplyAmber, sizePx: size * arrival.disc.sizeFactor,
    startMs: impactMs, durationMs: arrival.disc.durationMs, order: 20,
    pose: (local) => ({...to, scale: 0.7 + 0.3 * easeOutQuad(local), alpha: arrival.disc.alpha * (1 - local)}),
  }));
  for (let index = 0; index < arrival.burst.count; index += 1) {
    const angle = index / arrival.burst.count * TWO_PI + (rng() - 0.5) * 0.4;
    const distance = size * arrival.burst.spreadFactor * (0.7 + 0.5 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.supplyLight : COLORS.supplyAmber,
      sizePx: size * arrival.burst.sizeFactor, startMs: impactMs, durationMs: arrival.burst.lifeMs, order: 21,
      pose: (local) => ({
        x: to.x + Math.cos(angle) * distance * easeOutCubic(local),
        y: to.y + Math.sin(angle) * distance * easeOutCubic(local),
        scale: 1 - 0.5 * local,
        alpha: fadeOutFrom(local, 0.45),
        rotation: angle + local * 2,
      }),
    }));
  }

  return {totalMs: impactMs + tailMs, impactMs, elements, outlines: []};
};

/**
 * ОБРЫВ ПУТИ СНАБЖЕНИЯ: вспышка и искры в точке, где нить рвется
 *
 * @param {Object} params
 * @param {{x, y}} params.point — граница, докуда ферма дотянулась
 * @param {number} params.hexSize
 * @param {number} [params.delayMs] — когда нить дотянется до границы
 * @param {function(): number} [params.rng]
 */
export const buildSupplyBreakCastPlan = ({point, hexSize, delayMs = 0, rng = Math.random}) => {
  const {flash, ring, sparks} = SUPPLY_BREAK_CAST;
  const size = hexSize;
  const elements = [];

  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: COLORS.ember, sizePx: size * ring.sizeFactor,
    startMs: delayMs, durationMs: ring.durationMs, layer: 'ground',
    pose: (local) => ({...point, scale: 0.2 + 0.8 * easeOutCubic(local), alpha: ring.alpha * (1 - local)}),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.supplyLight, sizePx: size * flash.sizeFactor,
    startMs: delayMs, durationMs: flash.durationMs, order: 1,
    pose: (local) => ({...point, scale: 0.6 + 0.4 * easeOutQuad(local), alpha: flash.alpha * (1 - local)}),
  }));
  for (let index = 0; index < sparks.count; index += 1) {
    const angle = index / sparks.count * TWO_PI + (rng() - 0.5) * 0.6;
    const distance = size * sparks.spreadFactor * (0.5 + 0.7 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.supplyLight : COLORS.ember,
      sizePx: size * sparks.sizeFactor, startMs: delayMs, durationMs: sparks.lifeMs, order: 2,
      pose: (local) => ({
        x: point.x + Math.cos(angle) * distance * easeOutCubic(local),
        // Искры падают: нить здесь оборвалась
        y: point.y + Math.sin(angle) * distance * easeOutCubic(local) + size * sparks.fallFactor * local * local,
        scale: 1 - 0.6 * local,
        alpha: fadeOutFrom(local, 0.4),
        rotation: angle + local * 3,
      }),
    }));
  }

  const totalMs = delayMs + Math.max(flash.durationMs, ring.durationMs, sparks.lifeMs);
  return {totalMs, impactMs: 0, elements, outlines: []};
};

/**
 * РАЗОВЫЙ ПОКАЗ ДОХОДА: кольцо и восходящий поток искр от медальона
 *
 * @param {Object} params
 * @param {{x, y}} params.center — центр медальона
 * @param {number} params.hexSize
 * @param {boolean} [params.isSilver] — доход очками: серебро вместо золота
 * @param {function(): number} [params.rng]
 */
export const buildIncomeRevealCastPlan = ({center, hexSize, isSilver = false, rng = Math.random}) => {
  const {totalMs, aura, ring, stream} = INCOME_REVEAL_CAST;
  const size = hexSize;
  const main = isSilver ? COLORS.silver : COLORS.upgradeGold;
  const light = isSilver ? COLORS.silverLight : COLORS.upgradeLight;
  const deep = isSilver ? COLORS.silverDeep : COLORS.upgradeAmber;
  const elements = [];

  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: main, sizePx: size * aura.sizeFactor,
    startMs: 0, durationMs: aura.durationMs, layer: 'ground',
    pose: (local) => ({...center, scale: 0.7 + 0.3 * easeOutQuad(local), alpha: aura.alpha * fadeInOut(local, 0.2, 0.6)}),
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: main, sizePx: size * ring.sizeFactor,
    startMs: 0, durationMs: ring.durationMs, layer: 'ground', order: 1,
    pose: (local) => ({...center, scale: 0.2 + 0.8 * easeOutCubic(local), alpha: ring.alpha * (1 - local)}),
  }));
  pushRisingSpiral(elements, {
    centerX: center.x, groundY: center.y + size * 0.3, topY: center.y - size * stream.topLiftFactor, size, particles: stream, order: 2, rng,
    tints: [main, light, deep, light],
  });

  return {totalMs, impactMs: 0, elements, outlines: []};
};

// Отряд колдует: искры кружат вокруг него и сходятся
const pushSwirl = (elements, {center, size, count, durationMs, sizeFactor, startRadiusFactor, endRadiusFactor, turns, rng}) => {
  for (let index = 0; index < count; index += 1) {
    const startAngle = index / count * TWO_PI + (rng() - 0.5) * 0.6;
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.goblinsDark : COLORS.goblinsLight,
      sizePx: size * sizeFactor, startMs: 0, durationMs: durationMs + 60, order: 1,
      pose: (local) => {
        const radius = size * lerp(startRadiusFactor, endRadiusFactor, local);
        const angle = startAngle + turns * TWO_PI * local;
        return {
          x: center.x + Math.cos(angle) * radius,
          y: center.y + Math.sin(angle) * radius * 0.7,
          scale: 0.7 + 0.5 * Math.sin(local * Math.PI),
          alpha: fadeInOut(local, 0.25, 0.25),
          rotation: angle,
        };
      },
    }));
  }
};

// Искры гоблинов разлетаются из точки и всплывают
const pushGoblinSparks = (elements, {center, size, count, startMs, lifeMs, sizeFactor, spreadFactor, rng}) => {
  for (let index = 0; index < count; index += 1) {
    const angle = index / count * TWO_PI + (rng() - 0.5) * 0.5;
    const distance = size * spreadFactor * (0.7 + 0.6 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.goblinsDark : COLORS.goblinsLight,
      sizePx: size * sizeFactor, startMs, durationMs: lifeMs, order: 7,
      pose: (local) => {
        const spread = easeOutCubic(local);
        return {
          x: center.x + Math.cos(angle) * distance * spread,
          y: center.y + Math.sin(angle) * distance * spread * 0.7 - size * 0.25 * local,
          scale: 1 - 0.4 * local,
          alpha: fadeOutFrom(local, 0.4),
          rotation: angle + local,
        };
      },
    }));
  }
};

// Неудача гоблинов: серый клуб дыма и осыпающиеся обломки
const pushGoblinFailure = (elements, {center, size, startMs, puffSizeFactor, puffMs, debrisCount, debrisSizeFactor, debrisLifeMs, rng}) => {
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.PUFF, tint: COLORS.failSmoke, sizePx: size * puffSizeFactor,
    startMs, durationMs: puffMs, order: 6,
    pose: (local) => ({
      x: center.x,
      y: center.y - size * 0.18 * local,
      scale: 0.45 + 0.8 * easeOutQuad(local),
      alpha: 0.65 * (1 - local),
      rotation: 0.35 * local,
    }),
  }));
  for (let index = 0; index < debrisCount; index += 1) {
    const angle = -Math.PI * (0.15 + 0.7 * rng());
    const speed = size * (0.7 + 0.7 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.debris, sizePx: size * debrisSizeFactor,
      startMs, durationMs: debrisLifeMs, order: 7,
      pose: (local) => ({
        ...ballisticPoint(center, Math.cos(angle) * speed, Math.sin(angle) * speed, size * 3.2, local * debrisLifeMs / 1000),
        alpha: fadeOutFrom(local, 0.5),
      }),
    }));
  }
};

/**
 * ЛОВУШКА ГОБЛИНОВ — ячейку видит владелец ловушки: облачко блесток летит к
 * ней, и контур ячейки вспыхивает (успех) либо остается дым (провал)
 *
 * @param {Object} params
 * @param {{body: {x, y}}} params.attacker — гоблин
 * @param {{body: {x, y}, outlineCenter: {x, y}, outlinePoints: Array<{x, y}>}} params.target —
 *   ячейка ловушки: центр контура ловушки и вершины ячейки относительно центра
 * @param {number} params.hexSize
 * @param {boolean} params.success
 * @param {function(): number} [params.rng]
 */
export const buildGoblinsTrapCastPlan = ({attacker, target, hexSize, success, rng = Math.random}) => {
  const {swirl, flight, success: win, failure: lose} = GOBLINS_TRAP_CAST;
  const size = hexSize;
  const from = attacker.body;
  const to = target.outlineCenter || target.body;
  const lift = resolveArcLift(distanceBetween(from, to), size, flight);
  const impactMs = flight.startMs + flight.durationMs;
  const flightPoint = (share) => resolveArcPoint(from, to, lift, easeInOutSine(clamp01(share)));
  const elements = [];
  const outlines = [];

  pushSwirl(elements, {center: from, size, ...swirl, rng});

  // Облачко блесток с ярким ядром
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.goblins, sizePx: size * flight.sizeFactor,
    startMs: flight.startMs, durationMs: flight.durationMs, order: 4,
    pose: (local) => ({...flightPoint(local), scale: 0.6 + 0.4 * Math.sin(local * Math.PI), alpha: 0.8}),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.goblinsLight, sizePx: size * flight.sizeFactor * 0.6,
    startMs: flight.startMs, durationMs: flight.durationMs, order: 5,
    pose: (local) => ({...flightPoint(local), alpha: 0.95, rotation: local * Math.PI * 2}),
  }));
  for (let index = 0; index < flight.trailCount; index += 1) {
    const spawnMs = flight.startMs + index * flight.trailEveryMs;
    if (spawnMs >= impactMs) {
      break;
    }
    const origin = flightPoint((spawnMs - flight.startMs) / flight.durationMs);
    const driftX = (rng() - 0.5) * size * 0.18;
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.goblinsDark : COLORS.goblinsLight,
      sizePx: size * flight.trailSizeFactor, startMs: spawnMs, durationMs: flight.trailLifeMs, order: 3,
      pose: (local) => ({
        x: origin.x + driftX * local,
        y: origin.y + size * flight.trailFallFactor * local,
        scale: 1 - 0.5 * local,
        alpha: 1 - local,
        rotation: local * 2,
      }),
    }));
  }

  const outlinePoints = (target.outlinePoints || []).map(({x, y}) => ({
    x: x * win.outlineRadiusRatio,
    y: y * win.outlineRadiusRatio,
  }));

  let tailMs;
  if (success) {
    tailMs = win.tailMs;
    // Контур ячейки защелкивается: сжатый, с перехлестом до своего размера
    outlines.push({
      points: outlinePoints, color: COLORS.goblins, widthPx: size * win.outlineWidthFactor,
      startMs: impactMs, endMs: impactMs + win.outlineMs,
      pose: (local) => {
        const elapsed = local * win.outlineMs;
        const scale = elapsed < win.snapMs
          ? lerp(win.outlineFromScale, win.outlineOvershoot, easeOutCubic(elapsed / win.snapMs))
          : lerp(win.outlineOvershoot, 1, clamp01((elapsed - win.snapMs) / 120));
        return {...to, scale, alpha: fadeOutFrom(local, 0.5)};
      },
    });
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.goblinsLight, sizePx: size * win.burstSizeFactor,
      startMs: impactMs, durationMs: win.burstMs, order: 6,
      pose: (local) => ({...to, scale: 0.3 + 0.7 * easeOutCubic(local), alpha: 1 - local}),
    }));
    pushGoblinSparks(elements, {
      center: to, size, count: win.sparksCount, startMs: impactMs, lifeMs: win.sparksLifeMs,
      sizeFactor: win.sparksSizeFactor, spreadFactor: win.sparksSpreadFactor, rng,
    });
  } else {
    tailMs = lose.tailMs;
    outlines.push({
      points: outlinePoints, color: COLORS.failGray, widthPx: size * win.outlineWidthFactor * 0.6,
      startMs: impactMs, endMs: impactMs + lose.outlineMs,
      pose: (local) => ({...to, scale: 1, alpha: 0.7 * (1 - local)}),
    });
    pushGoblinFailure(elements, {center: to, size, startMs: impactMs, ...lose, rng});
  }

  return {totalMs: impactMs + tailMs, impactMs, elements, outlines};
};

/**
 * ЛОВУШКА ГОБЛИНОВ ГЛАЗАМИ СОПЕРНИКОВ: ячейку ловушки они не видят, поэтому
 * гоблин колдует на месте, и итог показывается на нем самом
 *
 * @param {Object} params
 * @param {{body: {x, y}}} params.attacker
 * @param {number} params.hexSize
 * @param {boolean} params.success
 * @param {function(): number} [params.rng]
 */
export const buildGoblinsHiddenTrapCastPlan = ({attacker, hexSize, success, rng = Math.random}) => {
  const {swirl, success: win, failure: lose, hidden} = GOBLINS_TRAP_CAST;
  const size = hexSize;
  const center = attacker.body;
  const impactMs = hidden.swirlMs;
  const elements = [];

  pushSwirl(elements, {
    center, size, ...swirl, count: hidden.count, durationMs: hidden.swirlMs - 60, turns: swirl.turns * 1.6, rng,
  });
  if (success) {
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: COLORS.goblinsLight, sizePx: size * hidden.resultSizeFactor,
      startMs: impactMs, durationMs: win.burstMs, order: 6,
      pose: (local) => ({...center, scale: 0.3 + 0.7 * easeOutCubic(local), alpha: 1 - local}),
    }));
    pushGoblinSparks(elements, {
      center, size, count: hidden.sparksCount, startMs: impactMs, lifeMs: win.sparksLifeMs,
      sizeFactor: win.sparksSizeFactor, spreadFactor: win.sparksSpreadFactor * 0.8, rng,
    });
  } else {
    pushGoblinFailure(elements, {
      center, size, startMs: impactMs, ...lose, puffSizeFactor: lose.puffSizeFactor * 0.8, debrisCount: 4, rng,
    });
  }

  return {totalMs: impactMs + hidden.tailMs, impactMs, elements, outlines: []};
};

/**
 * НАЙМ ОТРЯДА (см. UNIT_RECRUITMENT_CAST): момент попадания (impactMs) — отряд
 * проявился из света: тогда под двойником встает настоящий отряд, звучит найм,
 * всплывают монеты и расходится волна. Чем круче тип, тем больше слоев
 *
 * @param {Object} params
 * @param {{center: {x, y}, ground: {x, y}}} params.unit — опорные точки ячейки
 * @param {number} params.hexSize
 * @param {string} params.unitType — id типа отряда
 * @param {number} params.color — цвет игрока, 0xRRGGBB
 * @param {{image: {textureKey, sizePx}, outline: ?{textureKey, sizePx}, tint: number}} [params.art]
 *   двойник отряда; нет — проявляется только свет
 * @param {number} [params.hexRotation] — поворот шестиугольных колец под вершину ячейки
 * @param {function(): number} [params.rng]
 */
export const buildUnitRecruitmentCastPlan = ({unit, hexSize, unitType, color, art, hexRotation = 0, rng = Math.random}) => {
  const cast = resolveUnitRecruitmentCast(unitType);
  const {impactMs, tailMs, goldShare} = cast;
  const totalMs = impactMs + tailMs;
  const size = hexSize;
  const {center, ground} = unit;
  const light = mixColor(color, COLORS.white, 0.6);
  const elements = [];
  const hexScale = (radiusFactor) => resolveHexWaveScale(size * radiusFactor);
  const ringSizePx = HEX_WAVE_TEXTURE_SIZE;
  // Золото вперемешку с цветом игрока, равномерно по счету
  const checkIfGold = (index) => Math.floor((index + 1) * goldShare) > Math.floor(index * goldShare);
  const pickSparkTint = (index) => {
    if (checkIfGold(index)) {
      return index % 2 ? COLORS.upgradeLight : COLORS.upgradeGold;
    }
    return index % 2 ? light : color;
  };
  const afterImpact = (elapsedMs, durationMs) => clamp01((elapsedMs - impactMs) / durationMs);

  // ПОЛЕ ТЕМНЕЕТ: свет старших типов становится заметнее, а отряд — событием
  if (cast.dimAlpha > 0) {
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.black, sizePx: size * cast.dim.sizeFactor,
      startMs: 0, durationMs: totalMs, order: -10,
      pose: (local, elapsedMs) => {
        const leave = afterImpact(elapsedMs, tailMs);
        return {
          ...center,
          alpha: cast.dimAlpha * easeInOutSine(clamp01(elapsedMs / impactMs)) * (1 - easeInOutSine(clamp01((leave - cast.dim.holdShare) / (1 - cast.dim.holdShare)))),
        };
      },
    }));
  }

  // Свет на земле и контур самой ячейки разгораются к проявлению
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: color, sizePx: size * cast.auraSizeFactor,
    startMs: 0, durationMs: totalMs, layer: 'ground',
    pose: (local, elapsedMs) => {
      const share = clamp01(elapsedMs / impactMs);
      return {
        ...ground,
        scale: lerp(cast.aura.growFrom, 1, easeOutQuad(share)),
        alpha: cast.auraAlpha * (elapsedMs < impactMs ? share : 1 - afterImpact(elapsedMs, tailMs)),
      };
    },
  }));
  [CELL_OUTLINE_TEXTURE_KEYS.GLOW, HEX_WAVE_TEXTURE_KEYS.CORE].forEach((textureKey, index) => {
    elements.push(createElement({
      textureKey, tint: index ? light : color, sizePx: ringSizePx * hexScale(1),
      startMs: 0, durationMs: impactMs + cast.beamTailMs, layer: 'ground', order: 1 + index,
      pose: (local, elapsedMs) => ({
        ...center,
        rotation: hexRotation,
        alpha: elapsedMs < impactMs ? (elapsedMs / impactMs) ** 1.5 : 1 - afterImpact(elapsedMs, cast.beamTailMs),
      }),
    }));
  });

  // ЗНАК НА ЗЕМЛЕ: штриховые шестиугольники вращаются навстречу друг другу
  for (let index = 0; index < cast.runeCount; index += 1) {
    const radiusFactor = index ? cast.rune.innerRadiusFactor : cast.rune.radiusFactor;
    const direction = index ? -1 : 1;
    const isGold = cast.tier > 3 || index > 0;
    elements.push(createElement({
      textureKey: CELL_OUTLINE_TEXTURE_KEYS.CORE_DASHED, tint: isGold ? COLORS.upgradeGold : light, sizePx: ringSizePx * hexScale(radiusFactor),
      startMs: 0, durationMs: impactMs + cast.beamTailMs, layer: 'ground', order: 3 + index,
      pose: (local, elapsedMs) => {
        const leave = afterImpact(elapsedMs, cast.beamTailMs);
        return {
          ...center,
          scale: lerp(1.35, 1, easeOutCubic(clamp01(elapsedMs / impactMs))) * (1 + 0.5 * easeOutQuad(leave)),
          rotation: hexRotation + direction * cast.rune.turn * (elapsedMs / impactMs) ** 2,
          alpha: cast.rune.alpha * clamp01(elapsedMs / (impactMs * 0.35)) * (1 - leave),
        };
      },
    }));
  }

  // КОЛЬЦА сходятся к ячейке, с ускорением; последнее приходит к проявлению
  const ringLifeMs = impactMs * cast.chargeRing.lifeShare;
  for (let index = 0; index < cast.ringCount; index += 1) {
    const startMs = cast.ringCount > 1 ? (impactMs - ringLifeMs) * (index / (cast.ringCount - 1)) : impactMs - ringLifeMs;
    const isGold = checkIfGold(index);
    const ringPose = (local) => ({
      ...center,
      scale: lerp(cast.ringFromFactor, cast.chargeRing.toFactor, local * local),
      rotation: hexRotation,
      alpha: clamp01(local / 0.25) * (local > 0.9 ? (1 - local) / 0.1 : 1),
    });
    elements.push(createElement({
      textureKey: CELL_OUTLINE_TEXTURE_KEYS.GLOW, tint: isGold ? COLORS.upgradeGold : color, sizePx: ringSizePx * hexScale(1),
      startMs, durationMs: ringLifeMs, order: 1,
      pose: (local) => {
        const pose = ringPose(local);
        return {...pose, alpha: cast.ringGlowAlpha * pose.alpha};
      },
    }));
    elements.push(createElement({
      textureKey: HEX_WAVE_TEXTURE_KEYS.CORE, tint: isGold ? COLORS.upgradeLight : light, sizePx: ringSizePx * hexScale(1),
      startMs, durationMs: ringLifeMs, order: 2,
      pose: (local) => {
        const pose = ringPose(local);
        return {...pose, alpha: cast.ringCoreAlpha * pose.alpha};
      },
    }));
  }

  // ЛУЧИ за отрядом на проявлении: у старших два слоя вращаются навстречу
  for (let index = 0; index < cast.raysLayers; index += 1) {
    const direction = index ? -1 : 1;
    const hasGold = goldShare > 0;
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST,
      tint: index ? light : (hasGold ? COLORS.upgradeGold : color),
      sizePx: size * cast.raysSizeFactor * (index ? 0.76 : 1),
      startMs: impactMs - cast.rays.leadMs, durationMs: cast.rays.leadMs + cast.rays.lifeMs + cast.tier * 80, order: 3,
      pose: (local) => ({
        ...center,
        scale: 0.3 + 0.7 * easeOutCubic(clamp01(local * 2.5)),
        alpha: cast.raysAlpha * fadeInOut(local, 0.12, 0.55),
        rotation: direction * cast.rays.turn * local,
      }),
    }));
  }

  // СТОЛБ СВЕТА: узкий поднимается от земли, на проявлении вспыхивает шире и сходит
  // на нет. Картинка высотой во столько-то ширин, поэтому высота задается масштабом
  const beamWidth = size * cast.beamWidthFactor;
  const beamHeight = size * cast.beamHeightFactor;
  const beamStartMs = impactMs * cast.beam.appearShare;
  const beamPose = (widthPx, elapsedMs) => {
    const isGrowing = elapsedMs < impactMs;
    const growth = clamp01((elapsedMs - beamStartMs) / (impactMs - beamStartMs));
    const leave = afterImpact(elapsedMs, cast.beamTailMs);
    const heightShare = isGrowing ? lerp(0.3, 1, easeOutCubic(growth)) : 1 + 0.14 * easeOutQuad(leave);
    const height = beamHeight * heightShare;
    return {
      x: center.x,
      y: ground.y - height / 2,
      scaleX: isGrowing ? lerp(0.16, 1, easeInOutSine(growth)) : cast.beam.flareWidth * (1 - easeOutCubic(leave)),
      scaleY: height / (widthPx * LIGHT_BEAM_ASPECT),
      alpha: isGrowing ? clamp01(growth * 3) : 1 - leave * leave,
    };
  };
  [
    {widthPx: beamWidth, tint: color, order: 4},
    {widthPx: beamWidth * cast.beam.coreWidthShare, tint: mixColor(light, COLORS.white, 0.5), order: 5},
  ].forEach(({widthPx, tint, order}) => {
    elements.push(createElement({
      textureKey: HEX_WAVE_TEXTURE_KEYS.BEAM, tint, sizePx: widthPx,
      startMs: beamStartMs, durationMs: impactMs - beamStartMs + cast.beamTailMs, order,
      pose: (local, elapsedMs) => beamPose(widthPx, elapsedMs),
    }));
  });

  // ИСКРЫ сходятся к центру по спирали
  const moteLifeMs = impactMs * cast.motes.lifeShare;
  for (let index = 0; index < cast.moteCount; index += 1) {
    const angle = rng() * TWO_PI;
    const distance = size * cast.ringFromFactor * (0.55 + 0.45 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: pickSparkTint(index),
      sizePx: size * cast.motes.sizeFactor, startMs: (impactMs - moteLifeMs) * (index / cast.moteCount), durationMs: moteLifeMs, order: 6,
      pose: (local) => {
        const swirl = angle + cast.motes.turn * local;
        const radius = distance * (1 - easeInOutSine(local));
        return {
          x: center.x + Math.cos(swirl) * radius,
          y: center.y + Math.sin(swirl) * radius * 0.8,
          scale: 0.55 + 0.65 * local,
          alpha: fadeInOut(local, 0.2, 0.1),
          rotation: swirl,
        };
      },
    }));
  }

  // Шар света собирается там, где проявится отряд
  const gatherStartMs = impactMs * cast.gather.fromShare;
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: light, sizePx: size * cast.gather.sizeFactor * (1 + cast.tier * 0.12),
    startMs: gatherStartMs, durationMs: impactMs - gatherStartMs + 90, order: 7,
    pose: (local, elapsedMs) => ({
      ...center,
      scale: lerp(0.2, 1, easeInOutSine(clamp01((elapsedMs - gatherStartMs) / (impactMs - gatherStartMs)))),
      alpha: cast.gather.alpha * (elapsedMs < impactMs ? clamp01(local * 2.5) : 1 - afterImpact(elapsedMs, 90)),
    }),
  }));

  // ОТРЯД: белый силуэт вырастает из земли, перехлестывает и садится в свой размер,
  // наливаясь цветом. Настоящий отряд в момент проявления уже стоит под ним
  if (art?.image) {
    const materializeStartMs = impactMs - cast.unitMaterializeMs;
    const unitPose = (elapsedMs) => {
      if (elapsedMs < impactMs) {
        const share = clamp01((elapsedMs - materializeStartMs) / cast.unitMaterializeMs);
        return {
          x: center.x,
          y: center.y + size * cast.unit.riseFactor * (1 - easeOutCubic(share)),
          scale: lerp(cast.unit.fromScale, cast.unitOvershoot, easeOutCubic(share)),
          alpha: clamp01(share * 2.5),
          colorShare: 0,
        };
      }
      const share = afterImpact(elapsedMs, cast.unitSettleMs);
      return {
        ...center,
        scale: lerp(cast.unitOvershoot, 1, easeOutQuad(share)),
        alpha: share > 1 - cast.unit.fadeOutShare ? (1 - share) / cast.unit.fadeOutShare : 1,
        colorShare: easeOutQuad(clamp01(share * 1.4)),
      };
    };
    const unitDurationMs = cast.unitMaterializeMs + cast.unitSettleMs;
    if (art.outline) {
      elements.push(createElement({
        textureKey: art.outline.textureKey, sizePx: art.outline.sizePx, startMs: materializeStartMs, durationMs: unitDurationMs, order: 8,
        pose: (local, elapsedMs) => {
          const {colorShare, ...pose} = unitPose(elapsedMs);
          return pose;
        },
      }));
    }
    elements.push(createElement({
      textureKey: art.image.textureKey, sizePx: art.image.sizePx, startMs: materializeStartMs, durationMs: unitDurationMs, order: 9,
      pose: (local, elapsedMs) => {
        const {colorShare, ...pose} = unitPose(elapsedMs);
        return {...pose, tint: mixColor(COLORS.white, art.tint, colorShare)};
      },
    }));
  }

  // РАЗРЯД бьет сверху точно в момент проявления
  for (let index = 0; index < cast.strikeCount; index += 1) {
    const strikeHeight = size * cast.strikeHeightFactor;
    const topY = ground.y - strikeHeight;
    const strikeStartMs = impactMs - cast.strike.leadMs;
    [
      {widthPx: size * cast.strike.widthFactor * 2.4, tint: color, order: 10},
      {widthPx: size * cast.strike.widthFactor, tint: COLORS.white, order: 11},
    ].forEach(({widthPx, tint, order}) => {
      elements.push(createElement({
        textureKey: HEX_WAVE_TEXTURE_KEYS.BEAM, tint, sizePx: widthPx,
        startMs: strikeStartMs, durationMs: cast.strike.leadMs + cast.strike.holdMs, order,
        pose: (local, elapsedMs) => {
          const share = easeOutQuad(clamp01((elapsedMs - strikeStartMs) / cast.strike.leadMs));
          const height = strikeHeight * Math.max(0.02, share);
          return {
            x: center.x,
            y: topY + height / 2,
            scaleX: elapsedMs < impactMs ? 1 : 1 - afterImpact(elapsedMs, cast.strike.holdMs),
            scaleY: height / (widthPx * LIGHT_BEAM_ASPECT),
          };
        },
      }));
    });
  }

  // ПРОЯВЛЕНИЕ: плотный свет цвета игрока и белая вспышка
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SOFT_DISC, tint: light, sizePx: size * cast.bloom.sizeFactor * (1 + cast.tier * 0.1),
    startMs: impactMs, durationMs: cast.bloom.durationMs, order: 12,
    pose: (local) => ({...center, scale: 0.55 + 0.45 * easeOutCubic(local), alpha: cast.bloom.alpha * (1 - easeOutQuad(local))}),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.white, sizePx: size * cast.flashSizeFactor,
    startMs: impactMs - cast.flash.leadMs, durationMs: cast.flash.durationMs, order: 13, blend: 'add',
    pose: (local) => ({...center, scale: 0.6 + 0.4 * easeOutQuad(local), alpha: 1 - local}),
  }));
  for (let index = 0; index < cast.impactRingCount; index += 1) {
    elements.push(createElement({
      textureKey: ABILITY_CAST_TEXTURE_KEYS.RING, tint: pickSparkTint(index * 2 + 1),
      sizePx: size * cast.impactRingSizeFactor, startMs: impactMs + index * cast.impactRings.gapMs, durationMs: cast.impactRings.durationMs,
      layer: 'ground', order: 5,
      pose: (local) => ({...center, scale: 0.15 + 0.85 * easeOutCubic(local), alpha: cast.impactRings.alpha * (1 - local)}),
    }));
  }
  for (let index = 0; index < cast.burstCount; index += 1) {
    const angle = index / cast.burstCount * TWO_PI + (rng() - 0.5) * 0.4;
    const distance = size * cast.burstSpreadFactor * (0.7 + 0.5 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: pickSparkTint(index),
      sizePx: size * cast.burstSizeFactor, startMs: impactMs, durationMs: cast.burst.lifeMs, order: 14,
      pose: (local) => ({
        x: center.x + Math.cos(angle) * distance * easeOutCubic(local),
        y: center.y + Math.sin(angle) * distance * easeOutCubic(local),
        scale: 1 - 0.5 * local,
        alpha: fadeOutFrom(local, 0.45),
        rotation: angle + local * 2,
      }),
    }));
  }

  // ВОСХОДЯЩИЙ ПОТОК вокруг вставшего отряда
  if (cast.streamCount > 0) {
    const stream = [];
    pushRisingSpiral(stream, {
      centerX: center.x, groundY: ground.y, topY: center.y - size * cast.stream.topLiftFactor, size, order: 15, rng,
      particles: {...cast.stream, count: cast.streamCount, spawnMs: Math.max(0, tailMs - cast.stream.lifeMs)},
      tints: [pickSparkTint(0), COLORS.upgradeLight, COLORS.upgradeGold, light],
    });
    stream.forEach((element) => {
      elements.push({...element, startMs: element.startMs + impactMs, endMs: element.endMs + impactMs});
    });
  }

  // ЗОЛОТОЙ ВЕНЕЦ: звезды кружат над головой отряда и тают
  for (let crownIndex = 0; crownIndex < cast.crownCount; crownIndex += 1) {
    const crownY = center.y - size * cast.crown.liftFactor;
    const crownMs = tailMs * 0.9;
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.upgradeGold, sizePx: size * cast.crown.radiusFactor * 2.6,
      startMs: impactMs, durationMs: crownMs, order: 15,
      pose: (local) => ({x: center.x, y: crownY, scaleY: 0.42, scaleX: 1, alpha: 0.75 * fadeInOut(local, 0.15, 0.45)}),
    }));
    for (let index = 0; index < cast.crown.count; index += 1) {
      const startAngle = index / cast.crown.count * TWO_PI;
      elements.push(createElement({
        textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 2 ? COLORS.upgradeLight : COLORS.upgradeGold,
        sizePx: size * cast.crown.sizeFactor, startMs: impactMs, durationMs: crownMs, order: 16,
        pose: (local, elapsedMs) => {
          const phase = startAngle + cast.crown.turn * TWO_PI * local;
          return {
            x: center.x + Math.cos(phase) * size * cast.crown.radiusFactor,
            y: crownY + Math.sin(phase) * size * cast.crown.radiusFactor * cast.crown.flatten,
            scale: easeOutBack(clamp01((elapsedMs - impactMs) / cast.crown.appearMs)) * (0.8 + 0.2 * Math.sin(phase)),
            alpha: fadeOutFrom(local, 0.6),
            rotation: phase,
          };
        },
      }));
    }
  }

  // ДОЖДЬ ИСКР падает сверху и гаснет
  for (let index = 0; index < cast.rainCount; index += 1) {
    const x = center.x + (rng() - 0.5) * 2 * size * cast.rain.spreadFactor;
    const topY = center.y - size * cast.rain.topLiftFactor * (0.6 + 0.4 * rng());
    const spin = (rng() - 0.5) * 5;
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index % 3 ? COLORS.upgradeGold : COLORS.upgradeLight,
      sizePx: size * cast.rain.sizeFactor * (0.7 + 0.6 * rng()),
      startMs: impactMs + index / cast.rainCount * cast.rain.spawnMs, durationMs: cast.rain.lifeMs, order: 15,
      pose: (local) => ({
        x,
        y: topY + size * cast.rain.fallFactor * local * local,
        alpha: fadeInOut(local, 0.15, 0.4),
        rotation: spin * local,
      }),
    }));
  }

  const lastElementMs = elements.reduce((latest, element) => Math.max(latest, element.endMs), totalMs);
  return {
    totalMs: lastElementMs,
    impactMs,
    elements,
    outlines: [],
    shake: cast.shakeMs > 0 ? {atMs: impactMs, durationMs: cast.shakeMs, intensity: cast.shakeIntensity} : null,
  };
};

/**
 * РОСТ ДОХОДА РУИН: волна света от центра поля. Радиус растет равномерно — на
 * руинах она оказывается ровно тогда, когда на них сходит свет (см.
 * scenes/RuinsIncomeAnimation)
 *
 * @param {Object} params
 * @param {{x, y}} params.center — центр поля
 * @param {number} params.radius — радиус волны к концу
 * @param {number} params.durationMs
 * @param {number} [params.hexRotation] — поворот шестиугольника под вершину ячейки
 */
export const buildRuinsIncomeWaveCastPlan = ({center, radius, durationMs, hexRotation = 0}) => {
  const sizePx = HEX_WAVE_TEXTURE_SIZE * resolveHexWaveScale(radius);
  const elements = [
    {textureKey: HEX_WAVE_TEXTURE_KEYS.BAND, tint: COLORS.silver, alpha: 0.55, order: 0},
    {textureKey: HEX_WAVE_TEXTURE_KEYS.CORE, tint: COLORS.silverLight, alpha: 0.8, order: 1},
  ].map(({textureKey, tint, alpha, order}) => createElement({
    textureKey, tint, sizePx, startMs: 0, durationMs, blend: 'add', order,
    pose: (local) => ({
      ...center,
      scale: Math.max(0.01, local),
      rotation: hexRotation,
      alpha: alpha * fadeInOut(local, 0.04, 0.3),
    }),
  }));
  return {totalMs: durationMs, impactMs: 0, elements, outlines: []};
};

/**
 * РОСТ ДОХОДА РУИН: на руины сходит столб света, на волне они вспыхивают, вверх
 * поднимается поток серебряных искр. Колец здесь нет: кольца и лучи — у медальона,
 * а от руин они складывались бы с его кольцами и волной в сплошной засвет.
 * Размер — не от ячейки, а от того, как далеко камера: при отдаленном кадре
 * ячейка мала, и вспышка в ее размер потерялась бы
 *
 * @param {Object} params
 * @param {{x, y}} params.center — центр ячейки
 * @param {number} params.size — размер эффекта на поле
 * @param {boolean} [params.isSuperRuins] — великие руины: тяжелее обычных
 * @param {number} [params.particleShare] — доля искр: на поле с множеством руин меньше
 * @param {number} [params.hexRotation]
 * @param {function(): number} [params.rng]
 */
export const buildRuinsIncomeStrikeCastPlan = ({center, size: baseSize, isSuperRuins = false, particleShare = 1, hexRotation = 0, rng = Math.random}) => {
  const {strike} = RUINS_INCOME_ANIMATION;
  const {beamLeadMs, beamTailMs, totalMs, sparks} = strike;
  const size = baseSize * (isSuperRuins ? 1.3 : 1);
  const elements = [];

  // СТОЛБ: сверху вниз к земле, на ударе вспыхивает шире и сходит на нет
  const beamWidth = size * strike.beamWidthFactor;
  const beamHeight = size * strike.beamHeightFactor;
  const beamPose = (widthPx) => (local, elapsedMs) => {
    const share = clamp01(elapsedMs / beamLeadMs);
    const leave = clamp01((elapsedMs - beamLeadMs) / beamTailMs);
    return {
      x: center.x,
      y: center.y - beamHeight / 2,
      scaleX: elapsedMs < beamLeadMs ? lerp(0.15, 1, share) : 1.5 * (1 - easeOutCubic(leave)),
      scaleY: beamHeight / (widthPx * LIGHT_BEAM_ASPECT),
      alpha: elapsedMs < beamLeadMs ? share : 1 - leave * leave,
    };
  };
  [
    {widthPx: beamWidth, tint: COLORS.silver, order: 0},
    {widthPx: beamWidth * 0.45, tint: COLORS.silverLight, order: 1},
  ].forEach(({widthPx, tint, order}) => {
    elements.push(createElement({
      textureKey: HEX_WAVE_TEXTURE_KEYS.BEAM, tint, sizePx: widthPx, blend: 'add',
      startMs: 0, durationMs: beamLeadMs + beamTailMs, order, pose: beamPose(widthPx),
    }));
  });

  // ВСПЫШКА на ударе
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.silverLight, sizePx: size * strike.flashSizeFactor, blend: 'add',
    startMs: beamLeadMs, durationMs: strike.flashMs, order: 2,
    pose: (local) => ({...center, scale: 0.5 + 0.5 * easeOutCubic(local), alpha: strike.flashAlpha * fadeInOut(local, 0.08, 0.7)}),
  }));

  // ИСКРЫ поднимаются от руин после удара
  const spiral = [];
  pushRisingSpiral(spiral, {
    centerX: center.x,
    groundY: center.y + size * 0.3,
    topY: center.y - size * sparks.topLiftFactor,
    size,
    particles: {...sparks, count: Math.max(3, Math.round(sparks.count * particleShare))},
    order: 6,
    rng,
    tints: [COLORS.silverLight, COLORS.silver, COLORS.silverDeep, COLORS.silverLight],
  });
  spiral.forEach((element) => {
    elements.push({...element, startMs: element.startMs + beamLeadMs, endMs: element.endMs + beamLeadMs});
  });

  return {totalMs, impactMs: beamLeadMs, elements, outlines: []};
};

// Сколько частиц при доле бюджета: на поле с множеством руин меньше, но не ноль
const scaleParticleCount = (count, share) => Math.max(3, Math.round(count * share));

/**
 * РОСТ ДОХОДА РУИН: перерождение медальона. Пока он копит силу, к нему по спирали
 * сходятся искры; на ударе вспышка, расходятся кольца и разлетаются осколки, за
 * новым медальоном вращаются лучи, вверх идет поток серебра
 *
 * @param {Object} params
 * @param {{x, y}} params.center — центр медальона
 * @param {number} params.size — видимая сторона медальона на поле
 * @param {boolean} [params.isSuperRuins] — великие руины: тяжелее обычных
 * @param {number} [params.particleShare] — доля частиц
 * @param {number} [params.hexRotation]
 * @param {function(): number} [params.rng]
 */
export const buildRuinsIncomeRebirthCastPlan = ({center, size: baseSize, isSuperRuins = false, particleShare = 1, hexRotation = 0, rng = Math.random}) => {
  const {cast} = RUINS_INCOME_ANIMATION.rebirth;
  const impactMs = RUINS_INCOME_ANIMATION.strike.beamLeadMs;
  const {motes, flash, rays, rings, shards, stream} = cast;
  const size = baseSize * (isSuperRuins ? 1.25 : 1);
  const elements = [];
  const tints = [COLORS.silverLight, COLORS.silver, COLORS.silverLight, COLORS.silverDeep];

  // Искры сходятся к медальону
  const moteCount = scaleParticleCount(motes.count, particleShare);
  for (let index = 0; index < moteCount; index += 1) {
    const angle = index / moteCount * TWO_PI + (rng() - 0.5) * 0.5;
    const distance = size * motes.startRadiusFactor * (0.7 + 0.3 * rng());
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: tints[index % tints.length], sizePx: size * motes.sizeFactor, blend: 'add',
      startMs: 0, durationMs: impactMs, order: 0,
      pose: (local) => {
        const swirl = angle + motes.turn * local;
        const radius = distance * (1 - easeInOutSine(local));
        return {
          x: center.x + Math.cos(swirl) * radius,
          y: center.y + Math.sin(swirl) * radius,
          scale: 0.5 + 0.7 * local,
          alpha: fadeInOut(local, 0.25, 0.08),
          rotation: swirl,
        };
      },
    }));
  }

  // Лучи вращаются навстречу друг другу и держатся, пока новый медальон встает
  [1, -1].forEach((direction, index) => {
    elements.push(createElement({
      textureKey: MELEE_TEXTURE_KEYS.BURST, tint: index ? COLORS.silverLight : COLORS.silver,
      sizePx: size * rays.sizeFactor * (index ? rays.innerShare : 1), blend: 'add',
      startMs: impactMs, durationMs: rays.durationMs, order: 1,
      pose: (local) => ({
        ...center,
        scale: 0.25 + 0.75 * easeOutCubic(clamp01(local * 2.4)),
        alpha: rays.alpha * fadeInOut(local, 0.06, 0.55),
        rotation: direction * rays.turn * local,
      }),
    }));
  });

  // Вспышка, в которой рождается новый медальон
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.silverLight, sizePx: size * flash.sizeFactor, blend: 'add',
    startMs: impactMs, durationMs: flash.durationMs, order: 2,
    pose: (local) => ({...center, scale: 0.4 + 0.6 * easeOutCubic(local), alpha: flash.alpha * fadeInOut(local, 0.05, 0.75)}),
  }));

  // Тонкое кольцо расходится от медальона
  elements.push(createElement({
    textureKey: HEX_WAVE_TEXTURE_KEYS.CORE, tint: COLORS.silverLight,
    sizePx: HEX_WAVE_TEXTURE_SIZE * resolveHexWaveScale(size * rings.radiusFactor), blend: 'add',
    startMs: impactMs, durationMs: rings.durationMs, order: 3,
    pose: (local) => ({
      ...center,
      scale: 0.25 + 0.75 * easeOutCubic(local),
      rotation: hexRotation,
      alpha: rings.alpha * (1 - local),
    }),
  }));

  // Осколки прежнего медальона разлетаются и падают
  const shardCount = scaleParticleCount(shards.count, particleShare);
  for (let index = 0; index < shardCount; index += 1) {
    const angle = index / shardCount * TWO_PI + (rng() - 0.5) * 0.4;
    const distance = size * shards.distanceFactor * (0.6 + 0.5 * rng());
    elements.push(createElement({
      textureKey: index % 3 === 2 ? MELEE_TEXTURE_KEYS.GLOW : MELEE_TEXTURE_KEYS.BURST,
      tint: tints[index % tints.length], sizePx: size * shards.sizeFactor,
      startMs: impactMs, durationMs: shards.durationMs, order: 4,
      pose: (local) => {
        const travel = easeOutCubic(local);
        return {
          x: center.x + Math.cos(angle) * distance * travel,
          y: center.y + Math.sin(angle) * distance * travel + size * shards.fall * local * local,
          scale: 1 - 0.6 * local,
          alpha: 1 - local * local,
          rotation: angle + local * 3,
        };
      },
    }));
  }

  // Поток серебра вверх от нового медальона
  const spiral = [];
  pushRisingSpiral(spiral, {
    centerX: center.x,
    groundY: center.y + size * 0.2,
    topY: center.y - size * stream.topLiftFactor,
    size,
    particles: {...stream, count: scaleParticleCount(stream.count, particleShare)},
    order: 5,
    rng,
    tints,
  });
  spiral.forEach((element) => {
    elements.push({...element, startMs: element.startMs + impactMs, endMs: element.endMs + impactMs});
  });

  return {totalMs: cast.totalMs, impactMs, elements, outlines: []};
};
